import { TbCheck, TbChevronDown, TbChevronLeft, TbChevronRight, TbMaximize, TbX } from "solid-icons/tb";
import { createEffect, createSignal, For, type JSX, Match, onCleanup, onMount, Show, Switch } from "solid-js";
import { isServer, Portal } from "solid-js/web";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import type { ComparisonItem, KPITrend, Presentation, Slide, Theme } from "./presentationHtml";

// Slide layouts use fixed padding, type sizes and column counts, so a frame
// narrower than this renders the slide at this width and scales it down.
const SLIDE_DESIGN_WIDTH = 768;

const THEME_CLASSES: Record<Theme, string> = {
  professional: "bg-white text-gray-900 dark:bg-gray-900 dark:text-white",
  modern:
    "bg-gradient-to-br from-slate-50 to-blue-50 text-slate-900 dark:from-slate-900 dark:to-blue-950 dark:text-white",
  minimal: "bg-gray-50 text-gray-800 dark:bg-gray-950 dark:text-gray-100",
  dark: "bg-gray-900 text-white",
  vibrant: "bg-gradient-to-br from-purple-600 to-pink-500 text-white",
  executive: "bg-gradient-to-br from-slate-800 to-slate-900 text-white",
  startup: "bg-gradient-to-br from-indigo-500 to-purple-600 text-white",
  tech: "bg-gradient-to-br from-cyan-900 to-slate-900 text-cyan-50",
  finance: "bg-gradient-to-br from-emerald-800 to-slate-900 text-white",
  gradient: "bg-gradient-to-br from-rose-400 via-fuchsia-500 to-indigo-500 text-white",
  neon: "bg-black text-green-400",
  pastel: "bg-gradient-to-br from-pink-100 to-blue-100 text-slate-800",
  nature: "bg-gradient-to-br from-green-700 to-emerald-900 text-white",
};

interface ThemeOption {
  value: Theme;
  labelKey: string;
  category: "classic" | "corporate" | "creative";
}

const THEME_OPTIONS: ThemeOption[] = [
  { value: "professional", labelKey: "presentation_theme_professional", category: "classic" },
  { value: "modern", labelKey: "presentation_theme_modern", category: "classic" },
  { value: "minimal", labelKey: "presentation_theme_minimal", category: "classic" },
  { value: "dark", labelKey: "presentation_theme_dark", category: "classic" },
  { value: "vibrant", labelKey: "presentation_theme_vibrant", category: "classic" },
  { value: "executive", labelKey: "presentation_theme_executive", category: "corporate" },
  { value: "startup", labelKey: "presentation_theme_startup", category: "corporate" },
  { value: "tech", labelKey: "presentation_theme_tech", category: "corporate" },
  { value: "finance", labelKey: "category.finance", category: "corporate" },
  { value: "gradient", labelKey: "presentation_theme_gradient", category: "creative" },
  { value: "neon", labelKey: "presentation_theme_neon", category: "creative" },
  { value: "pastel", labelKey: "presentation_theme_pastel", category: "creative" },
  { value: "nature", labelKey: "presentation_theme_nature", category: "creative" },
];

const THEME_CATEGORY_KEYS = {
  classic: "presentation_theme_category_classic",
  corporate: "presentation_theme_category_corporate",
  creative: "presentation_theme_category_creative",
} as const;

interface ThemePickerProps {
  currentTheme: Theme;
  onThemeChange: (theme: Theme) => void;
  disabled?: boolean;
}

function ThemePicker(props: ThemePickerProps) {
  const rt = useChatRuntime();
  const [isOpen, setIsOpen] = createSignal(false);
  let containerRef: HTMLDivElement | undefined;

  const groupedThemes = () => {
    const groups: Record<string, ThemeOption[]> = { classic: [], corporate: [], creative: [] };
    for (const opt of THEME_OPTIONS) {
      groups[opt.category]?.push(opt);
    }
    return groups;
  };

  const handleClickOutside = (e: MouseEvent) => {
    if (containerRef && !containerRef.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  onMount(() => {
    document.addEventListener("click", handleClickOutside);
    onCleanup(() => document.removeEventListener("click", handleClickOutside));
  });

  return (
    <div ref={containerRef} class="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen())}
        disabled={props.disabled}
        class="flex items-center gap-1.5 rounded-lg border border-muted-border bg-tile px-2 py-1.5 text-sm transition-colors hover:bg-tile-hover disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={rt.t("presentation_theme_label")}
      >
        <div class={`size-3 rounded border border-primary-border ${THEME_CLASSES[props.currentTheme]}`} />
        <TbChevronDown size={14} class="text-secondary-text" />
      </button>

      <Show when={isOpen()}>
        <div class="absolute bottom-full left-0 z-50 mb-1 max-h-72 w-48 overflow-y-auto rounded-lg border border-primary-border bg-panel shadow-lg">
          <For each={Object.entries(groupedThemes())}>
            {([category, themes]) => (
              <div class="py-1">
                <div class="px-3 py-1 text-xs font-medium tracking-wide text-secondary-text uppercase">
                  {rt.t(THEME_CATEGORY_KEYS[category as keyof typeof THEME_CATEGORY_KEYS])}
                </div>
                <For each={themes}>
                  {(option) => (
                    <button
                      type="button"
                      class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors hover:bg-tile-hover"
                      onClick={() => {
                        props.onThemeChange(option.value);
                        setIsOpen(false);
                      }}
                    >
                      <div
                        class={`size-4 shrink-0 rounded border border-primary-border ${THEME_CLASSES[option.value]}`}
                      />
                      <span class="flex-1">{rt.t(option.labelKey)}</span>
                      <Show when={option.value === props.currentTheme}>
                        <TbCheck size={14} class="text-green-500" />
                      </Show>
                    </button>
                  )}
                </For>
              </div>
            )}
          </For>
        </div>
      </Show>
    </div>
  );
}

const getStatusClasses = (status?: "completed" | "active" | "pending") => {
  switch (status) {
    case "completed":
      return "bg-green-500 border-green-500";
    case "active":
      return "bg-blue-500 border-blue-500";
    default:
      return "bg-current/20 border-current/40";
  }
};

function TimelineSlide(props: { slide: Slide }) {
  const steps = () => props.slide.timeline?.steps ?? [];

  return (
    <div class="absolute inset-0 flex flex-col p-12">
      <Show when={props.slide.title}>
        <h2 class="mb-8 text-3xl font-bold">{props.slide.title}</h2>
      </Show>
      <div class="flex flex-1 items-center-safe justify-center-safe overflow-auto">
        <div class="flex items-start gap-4">
          <For each={steps()}>
            {(step, index) => (
              <div class="flex items-start">
                <div class="flex min-w-32 flex-col items-center">
                  <div
                    class={`flex size-8 items-center justify-center rounded-full border-2 text-sm ${getStatusClasses(step.status)}`}
                  >
                    <span class="text-xs">{index() + 1}</span>
                  </div>
                  <div class="mt-3 text-center">
                    <div class="text-lg font-semibold">{step.title}</div>
                    <Show when={step.date}>
                      <div class="mt-1 text-sm opacity-60">{step.date}</div>
                    </Show>
                    <Show when={step.description}>
                      <div class="mt-2 max-w-32 text-sm opacity-80">{step.description}</div>
                    </Show>
                  </div>
                </div>
                <Show when={index() < steps().length - 1}>
                  <div class="mt-4 h-0.5 w-16 bg-current/30" />
                </Show>
              </div>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

const getStyleClasses = (style?: "positive" | "negative" | "neutral") => {
  switch (style) {
    case "positive":
      return "border-green-500/30 bg-green-500/5";
    case "negative":
      return "border-red-500/30 bg-red-500/5";
    default:
      return "border-current/20 bg-current/5";
  }
};

function ComparisonSlide(props: { slide: Slide }) {
  const comparison = () => props.slide.comparison;

  const renderColumn = (item: ComparisonItem | undefined) => {
    if (!item) return null;
    return (
      <div class={`flex-1 rounded-lg border p-6 ${getStyleClasses(item.style)}`}>
        <div class="mb-4 flex items-center gap-2">
          <h3 class="text-xl font-semibold">{item.title}</h3>
        </div>
        <ul class="space-y-3">
          <For each={item.points}>
            {(point) => (
              <li class="flex items-start gap-2 text-lg">
                <span class="mt-2 size-1.5 shrink-0 rounded-full bg-current opacity-60" />
                <span>{point}</span>
              </li>
            )}
          </For>
        </ul>
      </div>
    );
  };

  return (
    <div class="absolute inset-0 flex flex-col p-12">
      <Show when={props.slide.title}>
        <h2 class="mb-8 text-3xl font-bold">{props.slide.title}</h2>
      </Show>
      <div class="flex flex-1 items-center-safe gap-6 overflow-auto">
        {renderColumn(comparison()?.left)}
        <Show when={comparison()?.versusLabel}>
          {(versusLabel) => (
            <div class="shrink-0 rounded-full bg-current/10 px-4 py-2 text-sm font-semibold">{versusLabel()}</div>
          )}
        </Show>
        {renderColumn(comparison()?.right)}
      </div>
    </div>
  );
}

const getTrendIcon = (direction?: "up" | "down" | "neutral") => {
  switch (direction) {
    case "up":
      return "+";
    case "down":
      return "-";
    default:
      return "";
  }
};

const getTrendColor = (trend?: KPITrend) => {
  if (!trend) return "opacity-60";
  if (trend.isPositive === true) return "text-green-500";
  if (trend.isPositive === false) return "text-red-500";
  switch (trend.direction) {
    case "up":
      return "text-green-500";
    case "down":
      return "text-red-500";
    default:
      return "opacity-60";
  }
};

function KPIDashboardSlide(props: { slide: Slide }) {
  const kpis = () => props.slide.kpis ?? [];

  return (
    <div class="absolute inset-0 flex flex-col p-12">
      <Show when={props.slide.title}>
        <h2 class="mb-8 text-3xl font-bold">{props.slide.title}</h2>
      </Show>
      <div class="grid flex-1 grid-cols-3 content-center-safe gap-6 overflow-auto">
        <For each={kpis()}>
          {(kpi) => (
            <div class="rounded-lg border border-current/20 bg-current/5 p-6">
              <div class="mb-2 text-4xl font-bold">{kpi.value}</div>
              <div class="text-lg opacity-80">{kpi.label}</div>
              <Show when={kpi.trend}>
                <div class={`mt-2 flex items-center gap-1 text-sm ${getTrendColor(kpi.trend)}`}>
                  <span>{getTrendIcon(kpi.trend?.direction)}</span>
                  <Show when={kpi.trend?.change}>{(change) => <span>{change()}</span>}</Show>
                </div>
              </Show>
              <Show when={kpi.comparisonText}>
                <div class="mt-1 text-xs opacity-60">{kpi.comparisonText}</div>
              </Show>
            </div>
          )}
        </For>
      </div>
    </div>
  );
}

function SlideContent(props: { slide: Slide }) {
  return (
    <Switch>
      <Match when={props.slide.type === "title-slide"}>
        <TitleSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "content-slide"}>
        <ContentSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "image-slide"}>
        <ImageSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "chart-slide"}>
        <ChartSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "two-column"}>
        <TwoColumnSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "section-header"}>
        <SectionHeaderSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "timeline-slide"}>
        <TimelineSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "comparison-slide"}>
        <ComparisonSlide slide={props.slide} />
      </Match>
      <Match when={props.slide.type === "kpi-dashboard"}>
        <KPIDashboardSlide slide={props.slide} />
      </Match>
    </Switch>
  );
}

function TitleSlide(props: { slide: Slide }) {
  return (
    <div class="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
      <h1 class="mb-6 text-5xl/tight font-bold">{props.slide.title}</h1>
      <Show when={props.slide.subtitle}>
        <p class="text-2xl opacity-80">{props.slide.subtitle}</p>
      </Show>
    </div>
  );
}

const getQuoteStyleClasses = (style?: "default" | "info" | "warning" | "success" | "tip") => {
  switch (style) {
    case "info":
      return "border-blue-500 bg-blue-500/10";
    case "warning":
      return "border-amber-500 bg-amber-500/10";
    case "success":
      return "border-green-500 bg-green-500/10";
    case "tip":
      return "border-purple-500 bg-purple-500/10";
    default:
      return "border-current/40 bg-current/5";
  }
};

function ContentSlide(props: { slide: Slide }) {
  return (
    <div class="absolute inset-0 flex flex-col p-12">
      <h2 class="mb-8 text-3xl font-bold">{props.slide.title}</h2>
      <div class="flex-1 overflow-auto">
        <div class="space-y-4">
          <For each={props.slide.content}>
            {(item) => (
              <Switch>
                <Match when={item.type === "bullet"}>
                  <div class="flex items-start gap-3 text-xl" style={{ "margin-left": `${(item.level ?? 0) * 24}px` }}>
                    <span class="mt-2 size-2 shrink-0 rounded-full bg-current opacity-60" />
                    <span>{item.text}</span>
                  </div>
                </Match>
                <Match when={item.type === "paragraph"}>
                  <p class="text-xl/relaxed">{item.text}</p>
                </Match>
                <Match when={item.type === "heading"}>
                  <h3 class="mt-6 text-2xl font-semibold">{item.text}</h3>
                </Match>
                <Match when={item.type === "code-block" && item.codeBlock}>
                  {(codeBlock) => (
                    <div class="overflow-hidden rounded-lg">
                      <Show when={codeBlock().title}>
                        <div class="border-b border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300">
                          {codeBlock().title}
                        </div>
                      </Show>
                      <pre class="overflow-x-auto bg-gray-900 p-4 text-gray-100">
                        <code class="font-mono text-sm whitespace-pre">{codeBlock().code}</code>
                      </pre>
                    </div>
                  )}
                </Match>
                <Match when={item.type === "quote" && item.quote}>
                  {(quote) => (
                    <blockquote class={`rounded-r-lg border-l-4 px-4 py-3 ${getQuoteStyleClasses(quote().style)}`}>
                      <Show when={quote().title}>
                        <div class="mb-2 font-semibold">{quote().title}</div>
                      </Show>
                      <p class="text-lg italic">{quote().text}</p>
                      <Show when={quote().attribution}>
                        <cite class="mt-2 block text-sm not-italic opacity-70">- {quote().attribution}</cite>
                      </Show>
                    </blockquote>
                  )}
                </Match>
                <Match when={item.type === "table" && item.table}>
                  {(table) => (
                    <div class="overflow-x-auto">
                      <table class="w-full border-collapse text-lg">
                        <Show when={table().headers?.length}>
                          <thead>
                            <tr class="border-b-2 border-current/30">
                              <For each={table().headers}>
                                {(header) => <th class="px-4 py-2 text-left font-semibold">{header}</th>}
                              </For>
                            </tr>
                          </thead>
                        </Show>
                        <tbody>
                          <For each={table().rows}>
                            {(row) => (
                              <tr class="border-b border-current/10">
                                <For each={row}>{(cell) => <td class="px-4 py-2">{cell}</td>}</For>
                              </tr>
                            )}
                          </For>
                        </tbody>
                      </table>
                      <Show when={table().caption}>
                        <p class="mt-2 text-center text-sm opacity-70">{table().caption}</p>
                      </Show>
                    </div>
                  )}
                </Match>
                <Match when={item.type === "kpi" && item.kpi}>
                  {(kpi) => (
                    <div class="inline-flex items-center gap-4 rounded-lg border border-current/20 bg-current/5 p-4">
                      <div>
                        <div class="text-3xl font-bold">{kpi().value}</div>
                        <div class="text-sm opacity-80">{kpi().label}</div>
                      </div>
                    </div>
                  )}
                </Match>
                <Match when={item.type === "icon"}>
                  {/* Icon type not supported - emojis don't render in PDF */}
                  <></>
                </Match>
              </Switch>
            )}
          </For>
        </div>
      </div>
    </div>
  );
}

// Resolves one slide artifact reference to a signed URL through the host seam.
function useDeckArtifactUrl(artifactId: () => string | undefined) {
  const rt = useChatRuntime();
  const [url, setUrl] = createSignal<string | undefined>();
  const [loading, setLoading] = createSignal(false);

  createEffect(() => {
    const id = artifactId();
    setUrl(undefined);
    const resolve = rt.actions.resolveArtifactUrls;
    if (!id || !resolve) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const load = async (): Promise<void> => {
      try {
        const results = await resolve([id]);
        if (cancelled) return;
        setUrl(results[0]?.url);
        setLoading(false);
      } catch {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    onCleanup(() => {
      cancelled = true;
    });
  });

  return { url, loading };
}

function ImageSlide(props: { slide: Slide }) {
  const { url: resolvedUrl, loading } = useDeckArtifactUrl(() =>
    props.slide.image?.artifactId && !props.slide.image?.url ? props.slide.image.artifactId : undefined
  );

  const imageUrl = () => props.slide.image?.url || resolvedUrl();

  return (
    <div class="absolute inset-0 flex flex-col items-center justify-center p-8">
      <Show when={props.slide.title}>
        <h2 class="mb-6 text-2xl font-bold">{props.slide.title}</h2>
      </Show>
      <figure class="flex max-h-[70%] flex-col items-center">
        <Show when={loading()}>
          <div class="flex h-48 w-64 items-center justify-center rounded-lg bg-black/10">
            <ProcessingSpinner class="size-8" />
          </div>
        </Show>
        <Show when={!loading() && imageUrl()}>
          <img
            src={imageUrl()}
            alt={props.slide.image?.alt ?? ""}
            class="max-h-full max-w-full rounded-lg object-contain shadow-lg"
          />
        </Show>
        <Show when={props.slide.image?.caption}>
          {(caption) => <figcaption class="mt-4 text-lg opacity-70">{caption()}</figcaption>}
        </Show>
      </figure>
    </div>
  );
}

function ChartSlide(props: { slide: Slide }) {
  const chartArtifactId = () => {
    const chart = props.slide.chart;
    return chart?.pngArtifactId || chart?.htmlArtifactId;
  };

  const { url: resolvedUrl, loading } = useDeckArtifactUrl(() =>
    chartArtifactId() && !props.slide.chart?.url ? chartArtifactId() : undefined
  );

  const chartUrl = () => props.slide.chart?.url || resolvedUrl();

  return (
    <div class="absolute inset-0 flex flex-col items-center justify-center p-8">
      <Show when={props.slide.title}>
        <h2 class="mb-6 text-2xl font-bold">{props.slide.title}</h2>
      </Show>
      <figure class="flex max-h-[70%] flex-col items-center">
        <Show when={loading()}>
          <div class="flex h-48 w-64 items-center justify-center rounded-lg bg-black/10">
            <ProcessingSpinner class="size-8" />
          </div>
        </Show>
        <Show when={!loading() && chartUrl()}>
          <img
            src={chartUrl()}
            alt={props.slide.chart?.alt ?? "Chart"}
            class="max-h-full max-w-full rounded-lg object-contain shadow-lg"
          />
        </Show>
        <Show when={props.slide.chart?.caption}>
          {(caption) => <figcaption class="mt-4 text-lg opacity-70">{caption()}</figcaption>}
        </Show>
      </figure>
    </div>
  );
}

function TwoColumnSlide(props: { slide: Slide }) {
  return (
    <div class="absolute inset-0 flex flex-col p-12">
      <h2 class="mb-8 text-3xl font-bold">{props.slide.title}</h2>
      <div class="grid flex-1 grid-cols-2 gap-8 overflow-auto">
        <div class="flex flex-col">
          <Show when={props.slide.leftColumn?.heading}>
            {(heading) => <h3 class="mb-4 border-b border-current/20 pb-2 text-xl font-semibold">{heading()}</h3>}
          </Show>
          <ul class="space-y-3">
            <For each={props.slide.leftColumn?.content}>
              {(item) => (
                <li class="flex items-start gap-2 text-lg">
                  <span class="mt-2 size-1.5 shrink-0 rounded-full bg-current opacity-60" />
                  <span>{item.text}</span>
                </li>
              )}
            </For>
          </ul>
        </div>
        <div class="flex flex-col">
          <Show when={props.slide.rightColumn?.heading}>
            {(heading) => <h3 class="mb-4 border-b border-current/20 pb-2 text-xl font-semibold">{heading()}</h3>}
          </Show>
          <ul class="space-y-3">
            <For each={props.slide.rightColumn?.content}>
              {(item) => (
                <li class="flex items-start gap-2 text-lg">
                  <span class="mt-2 size-1.5 shrink-0 rounded-full bg-current opacity-60" />
                  <span>{item.text}</span>
                </li>
              )}
            </For>
          </ul>
        </div>
      </div>
    </div>
  );
}

function SectionHeaderSlide(props: { slide: Slide }) {
  return (
    <div class="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
      <h1 class="mb-6 text-5xl/tight font-bold">{props.slide.title}</h1>
      <Show when={props.slide.subtitle}>
        <p class="text-2xl opacity-80">{props.slide.subtitle}</p>
      </Show>
    </div>
  );
}

export interface DeckViewerCtx {
  theme: () => Theme;
}

interface PresentationDeckViewerProps {
  presentation: Presentation;
  initialTheme?: Theme;
  /** Outer border + rounded shell; false when embedded under a host card (chief). */
  framed?: boolean;
  /** Hides the theme picker while slides are still streaming in. */
  isStreaming?: boolean;
  /** Host action buttons injected into the controls bar (bluesky: KB-save, Slides export). */
  actions?: (ctx: DeckViewerCtx) => JSX.Element;
  /** Host rows under the controls bar (confirmation banners, errors). */
  footer?: JSX.Element;
}

/**
 * The interactive deck viewer both apps share: one slide at a time with
 * prev/next + arrow keys, fullscreen (portal), and the theme picker. Hosts own
 * data loading and any export/save actions.
 */
export function PresentationDeckViewer(props: PresentationDeckViewerProps) {
  const rt = useChatRuntime();
  const [currentSlide, setCurrentSlide] = createSignal(0);
  const [isFullscreen, setIsFullscreen] = createSignal(false);
  const [overriddenTheme, setOverriddenTheme] = createSignal<Theme | null>(null);

  const slides = () => props.presentation.slides ?? [];
  const theme = () => overriddenTheme() ?? props.initialTheme ?? props.presentation.theme ?? "professional";
  const themeClasses = () => THEME_CLASSES[theme()];
  const framed = () => props.framed !== false;
  const [frameWidth, setFrameWidth] = createSignal(SLIDE_DESIGN_WIDTH);
  const slideWidth = () => Math.max(frameWidth(), SLIDE_DESIGN_WIDTH);
  const observeFrame = (el: HTMLDivElement) => {
    if (isServer) return;
    const observer = new ResizeObserver(() => setFrameWidth(el.clientWidth));
    observer.observe(el);
    onCleanup(() => observer.disconnect());
  };

  const goToPreviousSlide = () => {
    setCurrentSlide((c) => Math.max(0, c - 1));
  };

  const goToNextSlide = () => {
    setCurrentSlide((c) => Math.min(slides().length - 1, c + 1));
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen());
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    // Don't hijack arrow keys while the user is typing in an input,
    // textarea, or contenteditable surface (e.g. the chat composer).
    const active = document.activeElement as HTMLElement | null;
    if (active) {
      const tag = active.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || active.isContentEditable) return;
    }

    switch (e.key) {
      case "ArrowRight":
        e.preventDefault();
        goToNextSlide();
        break;
      case "ArrowLeft":
        e.preventDefault();
        goToPreviousSlide();
        break;
      case " ":
        if (!isFullscreen()) return;
        e.preventDefault();
        goToNextSlide();
        break;
      case "Escape":
        if (!isFullscreen()) return;
        setIsFullscreen(false);
        break;
    }
  };

  onMount(() => {
    if (isServer) return;
    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown));
  });

  const currentSlideData = () => slides()[currentSlide()];
  const ctx: DeckViewerCtx = { theme };

  const deck = (isFs: boolean) => {
    const prevButton = (
      <button
        type="button"
        onClick={goToPreviousSlide}
        disabled={currentSlide() === 0}
        class="rounded-lg border border-muted-border bg-tile p-2 transition-colors hover:bg-tile-hover disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={rt.t("presentation_previous_slide")}
      >
        <TbChevronLeft size={20} />
      </button>
    );
    const nextButton = (
      <button
        type="button"
        onClick={goToNextSlide}
        disabled={currentSlide() >= slides().length - 1}
        class="rounded-lg border border-muted-border bg-tile p-2 transition-colors hover:bg-tile-hover disabled:cursor-not-allowed disabled:opacity-50"
        aria-label={rt.t("presentation_next_slide")}
      >
        <TbChevronRight size={20} />
      </button>
    );
    const indicator = (
      <span class="text-sm whitespace-nowrap text-secondary-text" aria-live="polite">
        {currentSlide() + 1} / {slides().length}
      </span>
    );
    const themePicker = (
      <Show when={!props.isStreaming}>
        <ThemePicker currentTheme={theme()} onThemeChange={(newTheme) => setOverriddenTheme(newTheme)} />
      </Show>
    );
    const fullscreenButton = (
      <Show when={!isServer && !isFs}>
        <button
          type="button"
          onClick={toggleFullscreen}
          class="flex items-center gap-1.5 rounded-lg border border-muted-border bg-tile px-3 py-2 text-sm transition-colors hover:bg-tile-hover"
          aria-label={rt.t("presentation_fullscreen_enter")}
          title={rt.t("presentation_fullscreen_enter")}
        >
          <TbMaximize size={16} />
          <span class="@max-[520px]:hidden">{rt.t("presentation_fullscreen_label")}</span>
        </button>
      </Show>
    );

    return (
      <div
        data-renderer="presentation-deck-viewer"
        class={
          isFs
            ? "fixed inset-0 z-50 overflow-hidden bg-canvas text-base"
            : framed()
              ? "overflow-hidden rounded-xl border border-primary-border/50 text-base"
              : "overflow-hidden text-base"
        }
      >
        <Show when={isFs}>
          <button
            type="button"
            onClick={toggleFullscreen}
            class="absolute top-3 right-3 z-10 rounded-lg border border-muted-border bg-tile/80 p-2 backdrop-blur-sm transition-colors hover:bg-tile-hover"
            aria-label={rt.t("presentation_fullscreen_exit")}
            title={rt.t("presentation_fullscreen_exit")}
          >
            <TbX size={20} />
          </button>
        </Show>
        <div
          ref={isFs ? undefined : observeFrame}
          class={`relative overflow-hidden ${themeClasses()} ${isFs ? "h-[calc(100%-3.5rem)] w-full" : "aspect-video"} ${
            !isFs && framed() ? "rounded-t-xl" : ""
          }`}
          data-theme={theme()}
        >
          <Show
            when={isFs}
            fallback={
              <div
                class="absolute top-0 left-0 aspect-video origin-top-left"
                style={{ width: `${slideWidth()}px`, transform: `scale(${frameWidth() / slideWidth()})` }}
              >
                <Show when={currentSlideData()}>{(slide) => <SlideContent slide={slide()} />}</Show>
              </div>
            }
          >
            <Show when={currentSlideData()}>{(slide) => <SlideContent slide={slide()} />}</Show>
          </Show>
        </div>

        <div
          class={
            isFs
              ? "absolute inset-x-0 bottom-0 grid grid-cols-3 items-center gap-2 border-t-0 bg-panel/90 p-3 backdrop-blur-sm"
              : "@container border-t border-primary-border bg-panel p-3"
          }
        >
          <Show
            when={isFs}
            fallback={
              <div class="flex flex-wrap items-center justify-between gap-2 @max-[520px]:justify-center">
                {prevButton}
                <div class="flex items-center gap-2">
                  {indicator}
                  {themePicker}
                </div>
                <div class="flex flex-wrap items-center justify-center gap-2">
                  {fullscreenButton}
                  {props.actions?.(ctx)}
                  {nextButton}
                </div>
              </div>
            }
          >
            <div />
            <div class="flex items-center gap-2 justify-self-center">
              {prevButton}
              {indicator}
              {nextButton}
            </div>
            <div class="flex items-center gap-2 justify-self-end">
              {themePicker}
              {props.actions?.(ctx)}
            </div>
          </Show>
        </div>

        {props.footer}
      </div>
    );
  };

  return (
    <>
      <Show when={!isFullscreen()}>{deck(false)}</Show>
      <Show when={!isServer && isFullscreen()}>
        <Portal>{deck(true)}</Portal>
      </Show>
    </>
  );
}
